import React, { useState, useRef, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { OverlayPanel } from "primereact/overlaypanel";
import styled from "@emotion/styled";
import { SmallText } from "../../../components/core/Utility";
import { ColumnHeader } from "../../components/core/Table/ColumnHeader";
import _ from "lodash";
import { Wrapper } from "../../../components/core/Table/GridStyles";
import { Button } from "@vds/buttons";
import ViewDeliveryDateModal from "./ViewDeliveryDateModal";
import CreateWorkTicketModal from "./CreateWorkTicketModal";

const rowsDefault = [{ row: "" }];
const colsDefault = [{ cols: "" }];

const GridWrapper = styled(Wrapper)`
  .p-column-title {
    margin-right: 0.2rem !important;
  }
  .p-disabled {
    background-color: lightgrey !important;
    border: 1px solid lightgrey !important;
  }
  .p-datatable-table {
    width: 100% !important;
  }
  .p-datatable .p-datatable-tbody > tr {
    background-color: transparent !important;
  }
  .p-datatable .p-datatable-footer {
    background-color: transparent !important;
  }
  .p-datatable-scrollable .p-datatable-wrapper {
    position: relative;
    overflow: hidden;
    width: 100% !important;
  }
  .p-datatable-scrollable .p-datatable-thead > tr > th {
    display: block !important;
  }
  .p-datatable .p-datatable-tbody > tr > td {
    font-size: 0.75rem;
    font-weight: 400;
    line-height: 1rem;
    font-family: Verizon-NHG-eTX, Helvetica, Ariel, sans-serif;
    color: rgb(0, 0, 0);
    margin: 0px;
    text-decoration: none;
  }
`;

const tdStyle = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  alignItems: "start",
};
const MeatballButton = styled(Button)`
  background-color: transparent !important;
  border: none !important;
  box-shadow: none !important;
  width: 70px;
  color: black; /* Icon color when active/enabled */

  /* Style for hover and focus when NOT disabled */
  &:not(:disabled):hover,
  &:not(:disabled):focus {
    background-color: rgba(0, 0, 0, 0.08) !important;
  }

  /* Style for when the button IS disabled */
  &:disabled {
    background-color: transparent !important;
    color: lightgrey !important; /* Icon color when disabled */
  }
`;

const MeatballMenuOption = styled.div`
  padding: 8px 12px;
  cursor: pointer;
  font-size: 0.875rem;
  border-bottom: 1px solid #e9ecef;

  &:hover {
    background-color: #f8f9fa;
  }

  &:last-child {
    border-bottom: none;
  }
`;

export const WTACreateWorkTicketGrid = ({
  /** message in case of no records*/
  noDataMessage = "No data to display",
  /** data */
  rows = { rowsDefault },
  columns = { colsDefault },
  border,
  whiteSpace,
  flyOutMenu,
  resizableColumns = false,
  setUpdatedVehicles,
  createWorkTicketProgress,
  handleViewButtonClick,
  handleCreateButtonClick,
}) => {
  const dt = useRef(null);
  const [filters] = useState(null);
  const [updatedRows, setUpdatedRows] = useState([]);
  const buttonStatesRef = useRef({});
  const [confirmDeliveryDateModal, setConfirmDeliveryDateModal] = useState(false);
  const [pendingViewRow, setPendingViewRow] = useState(null);
  const [meatBallShow, setMeatBallShow] = useState(false);
  const [expandedRows, setExpandedRows] = useState(null);
  const meatballMenuRef = useRef(null);
  const [zeroInventoryModal, setZeroInventoryModal] = useState(false);
  const [pendingCreateRow, setPendingCreateRow] = useState(null);
  const [currentMeatballOptions, setCurrentMeatballOptions] = useState([]);

  useEffect(() => {
    const newRows = rows.map((row) => {
      const rowKey = row.rowKey; // Use rowKey from parent, don't regenerate
      const savedButtonState = buttonStatesRef.current[rowKey];
      return {
        ...row,
        viewButtonClicked: savedButtonState?.viewButtonClicked ?? row.viewButtonClicked ?? false,
      };
    });
    setUpdatedRows(newRows);
  }, [rows]);

  const updateKeyValueAtIndex = (array, idx, key /* value intentionally unused since we set true */) => {
    return array.map((item, i) => {
      if (i === idx) {
        return { ...item, [key]: true };
      } else {
        return { ...item, [key]: false };
      }
    });
  };

  const handleViewButtonState = (rowData) => {
    // Open date modal; do not flip to Create yet
    setPendingViewRow(rowData);
    setConfirmDeliveryDateModal(true);
  };

  const handleCreateButtonState = (rowData) => {
    if (createWorkTicketProgress) {
      return;
    }
    const hasZeroInventory = rowData.hardwareDetails?.some((hardware) => hardware.quantityATP === 0);
    //as per new requirementIf atlease one vehicle has sufficient ATP then show the split-zero-inventory option
    const checkOneVehicleCWT = rowData?.vehicleGroups?.createdGroup?.vehicles.length;

    if (hasZeroInventory && checkOneVehicleCWT) {
      // Open the zero inventory modal instead of proceeding directly
      setPendingCreateRow(rowData);
      setZeroInventoryModal(true);
      meatballMenuRef.current?.hide();
    } else {
      // Proceed with normal create work ticket flow
      const rowKey = rowData.rowKey;
      const saved = buttonStatesRef.current[rowKey] || {};
      const selectedReason = saved.selectedReason;
      meatballMenuRef.current?.hide();
      handleCreateButtonClick(
        rowData.bundleIds,
        rowData.addressId,
        rowData.contactId,
        rowData.lineType,
        selectedReason,
        null,
        null,
        rowData.workTicketId,
        rowData.isInstallationAddressSameAsShipping,
        rowData.installationAddressId,
        rowData.installationAddressContactId,
        rowData.isAddBMaterial
      );
    }
  };

  const getMeatballMenuOptions = (rowData) => {
    if (!rowData.viewButtonClicked || !rowData.hardwareDetails) {
      return [];
    }

    // Check if any hardware has quantityATP of 0
    const hasZeroInventory = rowData.hardwareDetails.some((hardware) => hardware.quantityATP === 0);
    //as per new requirementIf atlease one vehicle has sufficient ATP then show the split-zero-inventory option
    const checkOneVehicleCWT = rowData?.vehicleGroups?.createdGroup?.vehicles.length;

    const baseOptions = [
      {
        label: "Create work ticket",
        action: "create-work-ticket",
        onClick: () => handleCreateButtonState(rowData),
      },
    ];

    if (hasZeroInventory && checkOneVehicleCWT) {
      baseOptions.push({
        label: "Split due to zero inventory",
        action: "split-zero-inventory",
        onClick: () => handleSplitZeroInventory(rowData),
      });
    }

    return baseOptions;
  };

  const handleSplitZeroInventory = async (rowData) => {
    if (createWorkTicketProgress) {
      return;
    }
    if (!rowData.hardwareDetails || rowData.hardwareDetails.length === 0) {
      return;
    }

    try {
      // Separate hardware by ATP availability
      const hardwareWithATP = rowData.hardwareDetails.filter((hardware) => hardware.quantityATP > 0);
      const hardwareWithZeroATP = rowData.hardwareDetails.filter((hardware) => hardware.quantityATP === 0);

      // Instead of making two separate API calls, make one call with both groups
      // This will be handled by the CreateWorkticket component's logic for combined groups
      const rowKey = rowData.rowKey;
      const saved = buttonStatesRef.current[rowKey] || {};
      const selectedReason = saved.selectedReason;
      const selectedDeliveryDate = saved.selectedDeliveryDate;

      // Create a single call that will handle both groups internally
      await handleCreateButtonClick(
        rowData.bundleIds,
        rowData.addressId,
        rowData.contactId,
        rowData.lineType,
        selectedReason,
        {
          hardwareWithATP,
          hardwareWithZeroATP,
          splitZeroInventory: true, // Flag to indicate this is a split operation
        },
        null,
        rowData.workTicketId,
        selectedDeliveryDate,
        rowData.isInstallationAddressSameAsShipping,
        rowData.installationAddressId,
        rowData.installationAddressContactId,
        rowData.isAddBMaterial
      );
    } catch (error) {
      // Error handling for split zero inventory operation
    }

    meatballMenuRef.current?.hide();
  };

  const showMeatballMenu = (event, rowData) => {
    const options = getMeatballMenuOptions(rowData);
    if (options.length > 0) {
      setCurrentMeatballOptions(options);
      meatballMenuRef.current?.toggle(event);
    }
  };

  const meatballBodyTemplate = (rowData) => {
    const menuOptions = getMeatballMenuOptions(rowData);

    return (
      <React.Fragment>
        <MeatballButton
          size="medium"
          aria-label="More options"
          disabled={!rowData.viewButtonClicked || menuOptions.length === 0}
          onClick={(event) => showMeatballMenu(event, rowData)}
        >
          &#8942; {/* Vertical ellipsis */}
        </MeatballButton>

        <OverlayPanel ref={meatballMenuRef} id="meatball-overlay-panel" style={{ width: "200px" }}>
          <div role="listbox">
            {currentMeatballOptions.map((option, index) => (
              <MeatballMenuOption
                key={option.action}
                role="option"
                tabIndex="0"
                onClick={option.onClick}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    option.onClick();
                  }
                }}
              >
                {option.label}
              </MeatballMenuOption>
            ))}
          </div>
        </OverlayPanel>
      </React.Fragment>
    );
  };

  const getBodyToDisplay = (rowData, data) => {
    return <SmallText>{rowData[data.field]}</SmallText>;
  };

  const getNestedTableHardwareNames = (rowData /*, data */) => {
    if (rowData.viewButtonClicked === true) {
      const table1 = rowData?.hardwareDetails?.map((td, j) => {
        return (
          <tr key={j}>
            <td>
              <div style={{ display: "flex", flexDirection: "column" }}>{td.itemDescription}</div>
            </td>
          </tr>
        );
      });
      return table1;
    }
    return null;
  };
  const getNestedTableHardwareIds = (rowData /*, data */) => {
    if (rowData.viewButtonClicked === true) {
      const table1 = rowData?.hardwareDetails?.map((td, j) => {
        return (
          <tr key={j}>
            <td>
              <div style={{ display: "flex", flexDirection: "column" }}>{td.materialId}</div>
            </td>
          </tr>
        );
      });
      return table1;
    }
    return null;
  };
  const getNestedTableATPDate = (rowData /*, data */) => {
    if (rowData.viewButtonClicked === true) {
      const table1 = rowData?.hardwareDetails?.map((td, j) => {
        return (
          <tr key={j}>
            <td>
              <div style={{ display: "flex", flexDirection: "column" }}>{td.dateATP === null ? "" : td.dateATP}</div>
            </td>
          </tr>
        );
      });
      return table1;
    }
    return null;
  };
  const getNestedTableqRequestedQuantity = (rowData /*, data */) => {
    if (rowData.viewButtonClicked === true) {
      const table1 = rowData?.hardwareDetails?.map((td, j) => {
        return (
          <tr key={j}>
            <td>
              <div style={{ display: "flex", flexDirection: "column" }}>{td.totalQty}</div>
            </td>
          </tr>
        );
      });
      return table1;
    }
    return null;
  };
  const getNestedTableqQuantityATP = (rowData /*, data */) => {
    if (rowData.viewButtonClicked === true) {
      const table1 = rowData?.hardwareDetails?.map((td, j) => {
        return (
          <tr key={j}>
            <td>
              <div style={{ display: "flex", flexDirection: "column" }}>{td.quantityATP}</div>
            </td>
          </tr>
        );
      });
      return table1;
    }
    return null;
  };

  const getLineTypeDisplay = (rowData) => {
    return <SmallText>{rowData.lineType === "Reship" ? "Reship" : ""}</SmallText>;
  };

  const renderEmptyMessage = () => {
    return <SmallText>{noDataMessage}</SmallText>;
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <Button
        rounded
        outlined
        size="small"
        aria-label="View"
        onClick={() => handleViewButtonState(rowData)}
        style={{ backgroundColor: "white", color: "black" }}
        disabled={createWorkTicketProgress === true || (rowData.totalVehicles === rowData.needAttentionVehicles && rowData.hasKeyfob === false)}
      >
        View ATP
      </Button>
    );
  };

  return (
    <div css={{ overflow: "hidden", width: "100% !important" }}>
      <GridWrapper
        colSizes={{ mobile: 4, tablet: 12, desktop: 12 }}
        autoResizeHead={true}
        fontSize="14px"
        colGutter={true}
        whiteSpace={whiteSpace}
        border={border}
        width="100% !important"
      >
        <DataTable
          sortMode="multiple"
          removableSort
          ref={dt}
          resizableColumns={resizableColumns}
          columnResizeMode={resizableColumns ? "expand" : "fit"}
          scrollable
          scrollDirection="horizontal"
          responsiveLayout="scroll"
          filterDisplay="menu"
          emptyMessage={renderEmptyMessage()}
          filters={filters}
          value={updatedRows}
          pageLinkSize={3}
          sortOrder={1}
          tableStyle={{ minWidth: "50rem", width: "100% !important" }}
        >
          {columns.map((item) => {
            if (item.isMeatball) {
              return (
                <Column
                  key={item.key}
                  body={meatballBodyTemplate}
                  header=""
                  exportable={false}
                  style={{
                    "min-width": `${item.width || "50px"}`,
                    width: `${item.width || "50px"}`,
                    "min-height": "34px",
                    padding: "4px 0 4px 0!important",
                  }}
                ></Column>
              );
            }

            if (item.value === "viewORCreate") {
              return (
                <Column
                  header={<ColumnHeader col={item.key} ariaLabel={item.ariaLabel} />}
                  key={item.key}
                  body={actionBodyTemplate}
                  exportable={false}
                  style={{
                    "min-width": `${item.width || "200px"}`,
                    width: `${item.width || "200px"}`,
                    "min-height": "34px",
                    padding: "4px 0 4px 0!important",
                    backgroundColor: flyOutMenu && "transparent",
                    ...item.style,
                  }}
                ></Column>
              );
            } else if (item.value === "hardwareName") {
              return (
                <Column
                  header={<ColumnHeader col={item.key} ariaLabel={item.ariaLabel} />}
                  key={item.key}
                  body={getNestedTableHardwareNames}
                  exportable={false}
                  style={{
                    "min-width": `${item.width || "200px"}`,
                    width: `${item.width || "200px"}`,
                    "min-height": "34px",
                    padding: "4px 0 4px 0!important",
                    backgroundColor: flyOutMenu && "transparent",
                    ...item.style,
                    ...tdStyle,
                  }}
                ></Column>
              );
            } else if (item.value === "totalQty") {
              return (
                <Column
                  header={<ColumnHeader col={item.key} ariaLabel={item.ariaLabel} />}
                  key={item.key}
                  body={getNestedTableqRequestedQuantity}
                  exportable={false}
                  style={{
                    "min-width": `${item.width || "200px"}`,
                    width: `${item.width || "200px"}`,
                    "min-height": "34px",
                    padding: "4px 0 4px 0!important",
                    backgroundColor: flyOutMenu && "transparent",
                    ...item.style,
                    ...tdStyle,
                  }}
                ></Column>
              );
            } else if (item.value === "quantityATP") {
              return (
                <Column
                  header={<ColumnHeader col={item.key} ariaLabel={item.ariaLabel} />}
                  key={item.key}
                  body={getNestedTableqQuantityATP}
                  exportable={false}
                  style={{
                    "min-width": `${item.width || "200px"}`,
                    width: `${item.width || "200px"}`,
                    "min-height": "34px",
                    padding: "4px 0 4px 0!important",
                    backgroundColor: flyOutMenu && "transparent",
                    ...item.style,
                    ...tdStyle,
                  }}
                ></Column>
              );
            } else if (item.value === "dateATP") {
              return (
                <Column
                  header={<ColumnHeader col={item.key} ariaLabel={item.ariaLabel} />}
                  key={item.key}
                  body={getNestedTableATPDate}
                  exportable={false}
                  style={{
                    "min-width": `${item.width || "200px"}`,
                    width: `${item.width || "200px"}`,
                    "min-height": "34px",
                    padding: "4px 0 4px 0!important",
                    backgroundColor: flyOutMenu && "transparent",
                    ...item.style,
                    ...tdStyle,
                  }}
                ></Column>
              );
            } else if (item.value === "hardwareId") {
              return (
                <Column
                  header={<ColumnHeader col={item.key} ariaLabel={item.ariaLabel} />}
                  key={item.key}
                  body={getNestedTableHardwareIds}
                  exportable={false}
                  style={{
                    "min-width": `${item.width || "200px"}`,
                    width: `${item.width || "200px"}`,
                    "min-height": "34px",
                    padding: "4px 0 4px 0!important",
                    backgroundColor: flyOutMenu && "transparent",
                    ...item.style,
                    ...tdStyle,
                  }}
                ></Column>
              );
            } else if (item.value === "lineType") {
              return (
                <Column
                  header={<ColumnHeader col={item.key} ariaLabel={item.ariaLabel} />}
                  key={item.key}
                  body={getLineTypeDisplay}
                  exportable={false}
                  style={{
                    "min-width": `${item.width || "200px"}`,
                    width: `${item.width || "200px"}`,
                    "min-height": "34px",
                    padding: "4px 0 4px 0!important",
                    backgroundColor: flyOutMenu && "transparent",
                    ...item.style,
                    ...tdStyle,
                  }}
                ></Column>
              );
            } else {
              return (
                <Column
                  key={item.key}
                  field={item.value}
                  columnKey={item.key}
                  body={getBodyToDisplay}
                  header={<ColumnHeader col={item.key} ariaLabel={item.ariaLabel} />}
                  style={{
                    "min-width": `${item.width || "200px"}`,
                    width: `${item.width || "200px"}`,
                    "min-height": "34px",
                    padding: "4px 0 4px 0!important",
                    backgroundColor: flyOutMenu && "transparent",
                    ...item.style,
                  }}
                  sortable={item.isSortable}
                  filter={item.isFilterEnabled}
                  frozen={item.isFrozen}
                ></Column>
              );
            }
          })}
        </DataTable>
      </GridWrapper>
      {confirmDeliveryDateModal && (
        <ViewDeliveryDateModal
          opened={confirmDeliveryDateModal}
          setOpened={setConfirmDeliveryDateModal}
          isAddBMaterial={pendingViewRow?.isAddBMaterial === true}
          onContinue={(deliveryDate, reason) => {
            if (pendingViewRow) {
              // Now flip the button to Create for this row and stash selections
              let newRows = [...updatedRows];
              let index = newRows.findIndex((row) => {
                const addressMatch = row.addressId === pendingViewRow.addressId;
                const contactMatch = row.contactId === pendingViewRow.contactId;
                const bundleMatch = JSON.stringify(row.bundleIds) === JSON.stringify(pendingViewRow.bundleIds);
                const lineTypeMatch = (row.lineType ?? null) === (pendingViewRow.lineType ?? null);
                const workTicketMatch = (row.workTicketId ?? null) === (pendingViewRow.workTicketId ?? null);
                const isAddBMaterialMatch = (row.isAddBMaterial === true) === (pendingViewRow.isAddBMaterial === true);
                return addressMatch && contactMatch && bundleMatch && lineTypeMatch && workTicketMatch && isAddBMaterialMatch;
              });
              if (index !== -1) {
                newRows = updateKeyValueAtIndex(newRows, index, "viewButtonClicked", true);
                // Update ref for ALL rows - the clicked one gets true, others get false
                newRows.forEach((row) => {
                  const rowKey = row.rowKey;
                  const isClickedRow = row.rowKey === newRows[index].rowKey;
                  buttonStatesRef.current[rowKey] = {
                    ...(buttonStatesRef.current[rowKey] || {}),
                    viewButtonClicked: isClickedRow,
                    ...(isClickedRow && { selectedDeliveryDate: deliveryDate, selectedReason: reason }),
                  };
                });
                setUpdatedRows(newRows);
                setUpdatedVehicles(newRows);
              }
              // Proceed to load details with selected delivery date (reason is only for Create)
              handleViewButtonClick(
                pendingViewRow.bundleIds,
                pendingViewRow.addressId,
                pendingViewRow.contactId,
                pendingViewRow.lineType,
                deliveryDate,
                pendingViewRow.workTicketId,
                pendingViewRow.isAddBMaterial
              );
            }
            setConfirmDeliveryDateModal(false);
            setPendingViewRow(null);
          }}
        />
      )}

      {zeroInventoryModal && (
        <CreateWorkTicketModal
          createWorkTicketModal={zeroInventoryModal}
          setCreateWorkTicketModal={setZeroInventoryModal}
          disableActions={createWorkTicketProgress}
          handleWorkTicketCreation={(flag) => {
            if (pendingCreateRow) {
              const rowKey = pendingCreateRow.rowKey;
              const saved = buttonStatesRef.current[rowKey] || {};
              const selectedReason = saved.selectedReason;
              handleCreateButtonClick(
                pendingCreateRow.bundleIds,
                pendingCreateRow.addressId,
                pendingCreateRow.contactId,
                pendingCreateRow.lineType,
                selectedReason,
                null,
                flag,
                pendingCreateRow.workTicketId,
                pendingCreateRow.isInstallationAddressSameAsShipping,
                pendingCreateRow.installationAddressId,
                pendingCreateRow.installationAddressContactId,
                pendingCreateRow.isAddBMaterial
              );
            }
            setZeroInventoryModal(false);
            setPendingCreateRow(null);
          }}
          quantityValidationCheck={() => {
            // Handle quantity validation if needed
          }}
          handleSplitZeroInventory={() => {
            if (pendingCreateRow) {
              handleSplitZeroInventory(pendingCreateRow);
            }
            setZeroInventoryModal(false);
            setPendingCreateRow(null);
          }}
          lostInTransit={false}
        />
      )}
    </div>
  );
};
